import { isInformationOnlyResourceType, type ResourceType } from '@/services/api/api.types'

export type ContentAccessType = 'full_access' | 'information_only' | 'media_only'

export const CONTENT_ACCESS_LABELS: Record<ContentAccessType, string> = {
  full_access: 'Full Access',
  information_only: 'Information Only',
  media_only: 'Media Only',
}

type FormatFacts = {
  type: ResourceType | null | undefined
  /**
   * Whether the app has a viewer for this format. Absent means unknown, not no —
   * the backend only started recording it in C2-2617 and treats missing the same way.
   */
  showInAppSupported: boolean | null | undefined
}

/** Whether the file can be shown in the app at all, which is what the options hang off. */
export function isDisplayable({ type, showInAppSupported }: FormatFacts): boolean {
  if (isInformationOnlyResourceType(type)) return false
  return showInAppSupported !== false
}

/**
 * What the expert may choose for a supplementary file — decision 3. A format with no
 * viewer can only be Information Only, so the control is shown but has nothing to pick.
 */
export function contentAccessOptions(facts: FormatFacts): ContentAccessType[] {
  return isDisplayable(facts) ? ['full_access', 'information_only', 'media_only'] : ['information_only']
}

/**
 * The primary file's access, resolved rather than asked — decision 2.
 *
 * Never Media Only: the primary is what the Sidekick talks about, so it has to stay in
 * RAG, and `use_in_rag: false` is what de-indexes the step's FAQs. A stored Media Only
 * therefore resolves up to Full Access, which is what closes the transcript bypass.
 *
 * A stored `show_in_app: false` is kept instead, because resolving that up would make a
 * file the expert deliberately hid visible in the app, on the next save of a module that
 * predates this rule. Absent is not a choice and is not kept: the backend's resource
 * routes default `show_in_app` to true. Callers asking what a *format* allows, rather
 * than what a file stores, pass no flag — see `canSelectMediaMode`.
 */
export function resolvePrimaryAccess(facts: FormatFacts & { showInApp?: boolean | null }): ContentAccessType {
  if (!isDisplayable(facts)) return 'information_only'
  return facts.showInApp === false ? 'information_only' : 'full_access'
}

/** The two stored flags a choice maps to: `show_in_app` on the resource, `use_in_rag` on the step. */
export function contentAccessFlags(access: ContentAccessType): { showInApp: boolean; useInRag: boolean } {
  return {
    showInApp: access === 'full_access' || access === 'media_only',
    useInRag: access === 'full_access' || access === 'information_only',
  }
}

/**
 * Read a saved choice back out of the two flags. The inverse of `contentAccessFlags`.
 *
 * Absent reads as shown, matching `resolvePrimaryAccess` and the backend default. Reading
 * it as hidden labelled every pre-C2-2617 file "Information Only", and the file modal
 * saves that label back on the rename path, so a rename alone hid the file.
 */
export function resolveContentAccess(
  facts: FormatFacts & { showInApp: boolean | null | undefined; useInRag: boolean | null | undefined },
): ContentAccessType {
  if (!isDisplayable(facts)) return 'information_only'
  if (facts.showInApp === false) return 'information_only'
  return facts.useInRag === false ? 'media_only' : 'full_access'
}
